import React from 'react'

import { useFilter } from '@rewardops/ui'
import { getStartDate } from '@rewardops/utils'

import { useLocalisedOrdersSortOptions } from '~/components/hooks/filters/use-localized-orders-sort-options'
import { MapleSelect } from '~/components/maple/components/select'
import { useTranslation } from '~/app/i18n/client'

type SortOption = {
  key: string
  message: string
  type: 'order' | 'date'
  value?: number
}

const getFilterValues = (sort: SortOption) => {
  if (!sort) return {}
  if (sort.type === 'order') {
    return {
      sortBy: sort.key,
      startDate: undefined,
      endDate: undefined,
    }
  } else {
    return {
      sortBy: undefined,
      startDate: getStartDate(sort.value as any),
      endDate: undefined,
    }
  }
}

export const useOrdersQuery = () => {
  const { sort, currentPage, itemsPerPage, setCurrentPage } = useFilter()
  const localisedSortOptions = useLocalisedOrdersSortOptions()

  // Store the previous value of `sort`
  const prevSortRef = React.useRef<string>(sort)

  React.useEffect(() => {
    if (prevSortRef.current !== sort) {
      // Reset to page 1 only if the `sort` value changes
      setCurrentPage(1)
      // Update the ref to the new value of `sort`
      prevSortRef.current = sort
    }
  }, [sort, setCurrentPage])

  const queryParams = React.useMemo(() => {
    const filterValues = getFilterValues(
      localisedSortOptions.find((o) => o.key === sort) ?? {
        key: '',
        message: '',
        type: 'order',
      },
    )

    return {
      detailed: true,
      page: currentPage || 1,
      per_page_count: itemsPerPage || 10,
      ...(filterValues.sortBy && { sort_by: filterValues.sortBy }),
      ...(filterValues.startDate && { start_date: filterValues.startDate }),
      ...(filterValues.endDate ? { end_date: filterValues.endDate } : {}),
    }
  }, [sort, currentPage, itemsPerPage])

  return { queryParams }
}

export const SortMenu = () => {
  const { t } = useTranslation()
  const { sort, setSort } = useFilter()
  const localisedSortOptions = useLocalisedOrdersSortOptions()
  const defaultValue = localisedSortOptions?.find((o) => o.key === sort)?.key
  const options = localisedSortOptions.map((option) => ({
    key: option.key,
    value: option.message,
  }))

  return (
    <MapleSelect
      id="sort-select"
      className="w-fit"
      placeholder={t('filters.newest')}
      defaultValue={defaultValue}
      options={options}
      onValueChange={(s) => setSort(s)}
    />
  )
}
